import { ChatStore } from "@/stores/app/chatStore";
import { useWindowSize } from "@vueuse/core";
import { nextTick, onBeforeUnmount, onMounted, Ref, ref, watch, WatchSource } from "vue";
import { calcHeightToFill } from "./height";

/** O que precisamos da instância do `UseVirtualList` (template ref). */
type VirtualListInstance = { $el?: HTMLElement, scrollTo?: (index: number) => void };

/**
 * Altura reativa para o `UseVirtualList` preencher o espaço até o fim da viewport.
 *
 * O `UseVirtualList` do vueuse lê a prop `height` UMA vez, no setup — mudar a prop depois
 * não faz nada. Por isso a altura antes era aplicada desmontando e remontando a lista a
 * cada mudança de tamanho dela, o que fazia a lista inteira piscar e voltar ao topo.
 * Agora a altura vai por `:style` (fallthrough no elemento raiz, que vence o style interno
 * e é reativo), e a lista nunca é remontada: o próprio `useVirtualList` já recalcula
 * as linhas visíveis quando a lista troca ou o container muda de tamanho.
 *
 * Uso: `<UseVirtualList ref="vList" :list="lista" :style="{ height: listHeight }" ...>`
 * — a linha do slot precisa de `:key` pelo id do item: o `UseVirtualList` renderiza as
 * linhas por ÍNDICE, e sem a chave o componente da linha (menu aberto, modal, swipe)
 * passa a mostrar/agir sobre OUTRO item quando a lista ganha ou perde um elemento.
 */
export function vListCalcHeight(
    parentElementId: string,
    chatStore: ChatStore,
    config: {
        vList: Ref<VirtualListInstance | null>,
        list: WatchSource<unknown[]>,
        itemHeight?: number,
        triggersToRecalc?: WatchSource[],
        vListHeightBottomPadding?: number,
        vListHeightFallback?: string,
        pxToSubtract?: number,
    }
) {
    const fallback = config.vListHeightFallback || '80vh';
    const itemHeight = config.itemHeight ?? 88;
    const listHeight = ref(fallback);

    function recalc() {
        const bottomPadding = config.vListHeightBottomPadding || 12 * 2;
        const calculatedHeight = calcHeightToFill(parentElementId, chatStore.isWsConnected ? bottomPadding : 80 + bottomPadding, config.pxToSubtract);
        listHeight.value = calculatedHeight ?? fallback;
    }

    // O `top` do container muda sem que nada daqui saiba: o aviso de alto volume aparece/
    // colapsa, o cabeçalho mobile entra no breakpoint, a transição de contexto troca a
    // árvore. Todos esses redimensionam algum ANCESTRAL (a área da lista é flex), então
    // observar a cadeia de ancestrais cobre o que antes só se corrigia no próximo remount.
    // Recalcular não mexe no `top`, então a nossa própria mudança de altura não entra em laço.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => recalc()) : null;
    function observeAncestors() {
        observer?.disconnect();
        let el = document.getElementById(parentElementId)?.parentElement ?? null;
        while (el) {
            observer?.observe(el);
            el = el.parentElement;
        }
    }

    const { height: windowHeight } = useWindowSize();
    watch([windowHeight, () => chatStore.isWsConnected, ...(config.triggersToRecalc ?? [])], () => void nextTick(recalc));
    // A lista montar/desmontar é o sinal confiável de que o container existe (ou mudou de
    // lugar, ex.: depois do `out-in` da transição de contexto).
    watch(config.vList, () => void nextTick(() => { observeAncestors(); recalc(); }), { flush: 'post' });
    onMounted(() => void nextTick(() => { observeAncestors(); recalc(); }));
    onBeforeUnmount(() => observer?.disconnect());

    // Lista encolheu com o scroll lá embaixo (busca, filtro, finalização em lote): o
    // `useVirtualList` recalcula ANTES do patch, com o `scrollTop` antigo, e fatia além do
    // fim → zero linhas e wrapper com altura negativa (ignorada pelo browser, então o
    // scroll nunca é corrigido sozinho). Aqui, já depois do patch, trazemos o scroll para
    // dentro da lista nova; o `scrollTo` exposto recalcula as linhas na hora, sem frame vazio.
    watch(config.list, (list) => {
        const vList = config.vList.value;
        const el = vList?.$el;
        if (!el || !vList?.scrollTo || !list?.length) return;
        const maxScrollTop = Math.max(0, list.length * itemHeight - el.clientHeight);
        if (el.scrollTop > maxScrollTop) vList.scrollTo(Math.floor(maxScrollTop / itemHeight));
    }, { flush: 'post' });

    return { listHeight };
}
